import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Tag, 
  Plus, 
  X, 
  Search,
  Save,
  RefreshCw,
  Edit2,
  Trash2,
  Check,
  Globe,
  Languages,
  Building2,
  Cpu,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Alert,
  AlertDescription,
} from '../components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper to detect Amharic characters
const isAmharic = (text) => /[\u1200-\u137F]/.test(text);

// Memoized Keyword Chip Component for better performance
const KeywordChip = memo(function KeywordChip({ 
  keyword, 
  isAmharicKeyword, 
  isEditing, 
  editValue, 
  onEditValueChange,
  onStartEdit, 
  onCancelEdit, 
  onSaveEdit, 
  onDelete,
  company 
}) {
  if (isEditing) {
    return (
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '4px 8px',
        backgroundColor: 'hsl(var(--accent))',
        borderRadius: 6,
        border: '2px solid hsl(var(--primary))'
      }}>
        <input
          type="text"
          value={editValue}
          onChange={(e) => onEditValueChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSaveEdit();
            if (e.key === 'Escape') onCancelEdit();
          }}
          autoFocus
          style={{ 
            width: 150, 
            height: 28, 
            fontSize: 13,
            padding: '4px 8px',
            border: '1px solid hsl(var(--border))',
            borderRadius: 4,
            outline: 'none',
            fontFamily: isAmharicKeyword ? "'Noto Sans Ethiopic', system-ui, sans-serif" : 'inherit'
          }}
          data-testid="edit-keyword-input"
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <button 
              onClick={onSaveEdit}
              style={{ 
                height: 24, 
                width: 24, 
                padding: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              data-testid="save-edit-btn"
            >
              <Check size={14} style={{ color: '#16a34a' }} />
            </button>
          </TooltipTrigger>
          <TooltipContent>Save (Enter)</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button 
              onClick={onCancelEdit}
              style={{ 
                height: 24, 
                width: 24, 
                padding: 0,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <X size={14} style={{ color: '#ef4444' }} />
            </button>
          </TooltipTrigger>
          <TooltipContent>Cancel (Esc)</TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div 
      className={`keyword-chip ${company}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 10px',
        backgroundColor: company === 'alephtav' ? '#e3f2fd' : '#e8f5e9',
        borderRadius: 6,
        border: `1px solid ${company === 'alephtav' ? '#90caf9' : '#a5d6a7'}`,
        fontSize: 13,
        fontFamily: isAmharicKeyword ? "'Noto Sans Ethiopic', system-ui, sans-serif" : 'inherit',
        transition: 'all 0.2s'
      }}
      data-testid={`keyword-${keyword}`}
    >
      {isAmharicKeyword && <Languages size={12} style={{ color: '#f59e0b' }} />}
      <span>{keyword}</span>
      <div style={{ display: 'flex', gap: 2, marginLeft: 4 }}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onStartEdit}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 2,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                opacity: 0.6
              }}
              data-testid={`edit-${keyword}`}
            >
              <Edit2 size={12} />
            </button>
          </TooltipTrigger>
          <TooltipContent>Edit</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={onDelete}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 2,
                borderRadius: 4,
                display: 'flex',
                alignItems: 'center',
                opacity: 0.6,
                color: '#ef4444'
              }}
              data-testid={`delete-${keyword}`}
            >
              <X size={12} />
            </button>
          </TooltipTrigger>
          <TooltipContent>Delete</TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
});

function Keywords() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeCompany, setActiveCompany] = useState('alephtav');
  const [activeLanguage, setActiveLanguage] = useState('english');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Keywords state
  const [keywords, setKeywords] = useState({
    alephtav: { english: [], amharic: [] },
    mautech: { english: [], amharic: [] }
  });
  
  // New keyword input - separate state to avoid re-renders
  const [newKeyword, setNewKeyword] = useState('');
  
  // Edit state
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [editValue, setEditValue] = useState('');
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Track unsaved changes
  const [hasChanges, setHasChanges] = useState({
    alephtav: false,
    mautech: false
  });

  useEffect(() => {
    fetchKeywords();
  }, []);

  const fetchKeywords = async () => {
    try {
      const [alephtavRes, mautechRes] = await Promise.all([
        fetch(`${API_URL}/api/keywords/ALEPHTAV`),
        fetch(`${API_URL}/api/keywords/MAU_TECH`)
      ]);

      const alephtavData = await alephtavRes.json();
      const mautechData = await mautechRes.json();

      const alephtavKeywords = alephtavData.keywords || [];
      const mautechKeywords = mautechData.keywords || [];

      setKeywords({
        alephtav: {
          english: alephtavKeywords.filter(k => !isAmharic(k)),
          amharic: alephtavKeywords.filter(k => isAmharic(k))
        },
        mautech: {
          english: mautechKeywords.filter(k => !isAmharic(k)),
          amharic: mautechKeywords.filter(k => isAmharic(k))
        }
      });
      
      setHasChanges({ alephtav: false, mautech: false });
    } catch (error) {
      console.error('Error fetching keywords:', error);
      toast.error('Failed to load keywords');
    } finally {
      setLoading(false);
    }
  };

  // Memoized filtered keywords
  const filteredKeywords = useMemo(() => {
    const list = keywords[activeCompany][activeLanguage];
    if (!searchQuery) return list;
    
    const query = searchQuery.toLowerCase();
    return list.filter(k => k.toLowerCase().includes(query));
  }, [keywords, activeCompany, activeLanguage, searchQuery]);

  // Handlers with useCallback to prevent unnecessary re-renders
  const handleAddKeyword = useCallback(() => {
    const keyword = newKeyword.trim();
    if (!keyword) {
      toast.error('Please enter a keyword');
      return;
    }

    const currentList = keywords[activeCompany][activeLanguage];
    
    // Check for duplicates
    const isDuplicate = currentList.some(k => 
      activeLanguage === 'english' 
        ? k.toLowerCase() === keyword.toLowerCase()
        : k === keyword
    );
    
    if (isDuplicate) {
      toast.error('This keyword already exists');
      return;
    }

    // Validate language match
    const keywordIsAmharic = isAmharic(keyword);
    if (activeLanguage === 'english' && keywordIsAmharic) {
      toast.error('This appears to be an Amharic keyword. Switch to the Amharic tab.');
      return;
    }
    if (activeLanguage === 'amharic' && !keywordIsAmharic) {
      toast.error('This appears to be an English keyword. Switch to the English tab.');
      return;
    }

    setKeywords(prev => ({
      ...prev,
      [activeCompany]: {
        ...prev[activeCompany],
        [activeLanguage]: [...prev[activeCompany][activeLanguage], keyword]
      }
    }));
    
    setNewKeyword('');
    setHasChanges(prev => ({ ...prev, [activeCompany]: true }));
    toast.success(`Added "${keyword}"`);
  }, [newKeyword, keywords, activeCompany, activeLanguage]);

  const handleDeleteKeyword = useCallback((keyword) => {
    setKeywords(prev => ({
      ...prev,
      [activeCompany]: {
        ...prev[activeCompany],
        [activeLanguage]: prev[activeCompany][activeLanguage].filter(k => k !== keyword)
      }
    }));
    
    setDeleteConfirm(null);
    setHasChanges(prev => ({ ...prev, [activeCompany]: true }));
    toast.success(`Deleted "${keyword}"`);
  }, [activeCompany, activeLanguage]);

  const handleStartEdit = useCallback((keyword) => {
    setEditingKeyword(keyword);
    setEditValue(keyword);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingKeyword(null);
    setEditValue('');
  }, []);

  const handleSaveEdit = useCallback((oldKeyword) => {
    const newKeywordValue = editValue.trim();
    
    if (!newKeywordValue) {
      toast.error('Keyword cannot be empty');
      return;
    }
    
    if (newKeywordValue === oldKeyword) {
      setEditingKeyword(null);
      setEditValue('');
      return;
    }

    const currentList = keywords[activeCompany][activeLanguage];
    const isDuplicate = currentList.some(k => 
      k !== oldKeyword && (
        activeLanguage === 'english' 
          ? k.toLowerCase() === newKeywordValue.toLowerCase()
          : k === newKeywordValue
      )
    );
    
    if (isDuplicate) {
      toast.error('This keyword already exists');
      return;
    }

    setKeywords(prev => ({
      ...prev,
      [activeCompany]: {
        ...prev[activeCompany],
        [activeLanguage]: prev[activeCompany][activeLanguage].map(k => 
          k === oldKeyword ? newKeywordValue : k
        )
      }
    }));
    
    setEditingKeyword(null);
    setEditValue('');
    setHasChanges(prev => ({ ...prev, [activeCompany]: true }));
    toast.success(`Updated keyword`);
  }, [editValue, keywords, activeCompany, activeLanguage]);

  const handleSave = useCallback(async (company) => {
    setSaving(true);
    try {
      const companyKey = company === 'alephtav' ? 'ALEPHTAV' : 'MAU_TECH';
      const allKeywords = [
        ...keywords[company].english,
        ...keywords[company].amharic
      ];

      const response = await fetch(`${API_URL}/api/keywords/${companyKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keywords: allKeywords })
      });

      if (response.ok) {
        setHasChanges(prev => ({ ...prev, [company]: false }));
        toast.success(`${company === 'alephtav' ? 'Alephtav' : 'Mau Tech'} keywords saved!`);
      } else {
        toast.error('Failed to save keywords');
      }
    } catch (error) {
      console.error('Error saving keywords:', error);
      toast.error('Error saving keywords');
    } finally {
      setSaving(false);
    }
  }, [keywords]);

  const totalKeywords = keywords[activeCompany].english.length + keywords[activeCompany].amharic.length;

  if (loading) {
    return (
      <div className="loading-spinner" data-testid="keywords-loading">
        <RefreshCw className="animate-spin" size={32} />
        <p>Loading keywords...</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="keywords-page" data-testid="keywords-page">
        <div className="section-header">
          <div>
            <h2 className="section-title">Classification Keywords</h2>
            <p style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, marginTop: 4 }}>
              Manage keywords used for searching and classifying tenders
            </p>
          </div>
        </div>

        {/* Info Alert */}
        <Alert style={{ marginBottom: 24, borderColor: '#3b82f6', backgroundColor: '#eff6ff' }}>
          <Info size={18} style={{ color: '#3b82f6' }} />
          <AlertDescription style={{ color: '#1e40af' }}>
            These keywords are used for <strong>both</strong> searching tender sites AND classifying results. 
            Changes take effect on the next scraper run.
          </AlertDescription>
        </Alert>

        {/* Company Tabs */}
        <Tabs value={activeCompany} onValueChange={setActiveCompany}>
          <TabsList className="company-tabs-list" style={{ marginBottom: 24 }}>
            <TabsTrigger value="alephtav" className="company-tab" data-testid="tab-alephtav">
              <Building2 size={16} />
              <span>Alephtav</span>
              <Badge variant="secondary" style={{ marginLeft: 8 }}>
                {keywords.alephtav.english.length + keywords.alephtav.amharic.length}
              </Badge>
              {hasChanges.alephtav && (
                <span style={{ marginLeft: 4, color: '#f59e0b' }}>•</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="mautech" className="company-tab" data-testid="tab-mautech">
              <Cpu size={16} />
              <span>Mau Tech</span>
              <Badge variant="secondary" style={{ marginLeft: 8 }}>
                {keywords.mautech.english.length + keywords.mautech.amharic.length}
              </Badge>
              {hasChanges.mautech && (
                <span style={{ marginLeft: 4, color: '#f59e0b' }}>•</span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Content for each company */}
          {['alephtav', 'mautech'].map(company => (
            <TabsContent key={company} value={company}>
              <Card>
                <CardHeader>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <CardTitle style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Tag size={20} />
                        {company === 'alephtav' ? 'Alephtav' : 'Mau Tech'} Keywords
                      </CardTitle>
                      <CardDescription>
                        {company === 'alephtav' 
                          ? 'Keywords for consultancy, IT services, and software development'
                          : 'Keywords for IT hardware, equipment supply, and procurement'
                        }
                      </CardDescription>
                    </div>
                    <Button 
                      onClick={() => handleSave(company)}
                      disabled={saving || !hasChanges[company]}
                      data-testid={`save-${company}-btn`}
                    >
                      <Save size={16} />
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Language Tabs */}
                  <Tabs value={activeLanguage} onValueChange={setActiveLanguage}>
                    <TabsList style={{ marginBottom: 16 }}>
                      <TabsTrigger value="english" data-testid="lang-english">
                        <Globe size={14} />
                        <span style={{ marginLeft: 4 }}>English</span>
                        <Badge variant="outline" style={{ marginLeft: 8 }}>
                          {keywords[company].english.length}
                        </Badge>
                      </TabsTrigger>
                      <TabsTrigger value="amharic" data-testid="lang-amharic">
                        <Languages size={14} />
                        <span style={{ marginLeft: 4 }}>አማርኛ (Amharic)</span>
                        <Badge variant="outline" style={{ marginLeft: 8 }}>
                          {keywords[company].amharic.length}
                        </Badge>
                      </TabsTrigger>
                    </TabsList>

                    {['english', 'amharic'].map(lang => (
                      <TabsContent key={lang} value={lang}>
                        {/* Add New Keyword */}
                        <div style={{ 
                          display: 'flex', 
                          gap: 12, 
                          marginBottom: 20,
                          padding: 16,
                          backgroundColor: 'hsl(var(--muted) / 0.3)',
                          borderRadius: 8,
                          border: '1px dashed hsl(var(--border))'
                        }}>
                          <input
                            type="text"
                            placeholder={lang === 'amharic' ? 'አዲስ ቁልፍ ቃል ያስገቡ...' : 'Enter new keyword...'}
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddKeyword()}
                            style={{ 
                              flex: 1,
                              padding: '8px 12px',
                              fontSize: 14,
                              border: '1px solid hsl(var(--border))',
                              borderRadius: 6,
                              outline: 'none',
                              fontFamily: lang === 'amharic' ? "'Noto Sans Ethiopic', system-ui, sans-serif" : 'inherit'
                            }}
                            data-testid="new-keyword-input"
                          />
                          <Button onClick={handleAddKeyword} data-testid="add-keyword-btn">
                            <Plus size={16} />
                            Add Keyword
                          </Button>
                        </div>

                        {/* Search */}
                        <div style={{ marginBottom: 16 }}>
                          <div style={{ position: 'relative' }}>
                            <Search size={16} style={{ 
                              position: 'absolute', 
                              left: 12, 
                              top: '50%', 
                              transform: 'translateY(-50%)',
                              color: 'hsl(var(--muted-foreground))'
                            }} />
                            <input
                              type="text"
                              placeholder={`Search ${keywords[company][lang].length} keywords...`}
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              style={{ 
                                width: '100%',
                                padding: '8px 12px 8px 40px',
                                fontSize: 14,
                                border: '1px solid hsl(var(--border))',
                                borderRadius: 6,
                                outline: 'none'
                              }}
                              data-testid="search-keywords-input"
                            />
                          </div>
                        </div>

                        {/* Keywords Grid */}
                        <div style={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 8,
                          maxHeight: 400,
                          overflowY: 'auto',
                          padding: 4
                        }}>
                          {filteredKeywords.length === 0 ? (
                            <div style={{ 
                              width: '100%', 
                              textAlign: 'center', 
                              padding: 40,
                              color: 'hsl(var(--muted-foreground))'
                            }}>
                              <AlertCircle size={32} style={{ margin: '0 auto 12px' }} />
                              <p>{searchQuery ? 'No keywords match your search' : 'No keywords added yet'}</p>
                              <p style={{ fontSize: 13, marginTop: 4 }}>
                                {!searchQuery && 'Add keywords using the input above'}
                              </p>
                            </div>
                          ) : (
                            filteredKeywords.map((keyword, index) => (
                              <KeywordChip
                                key={`${keyword}-${index}`}
                                keyword={keyword}
                                isAmharicKeyword={lang === 'amharic'}
                                isEditing={editingKeyword === keyword}
                                editValue={editValue}
                                onEditValueChange={setEditValue}
                                onStartEdit={() => handleStartEdit(keyword)}
                                onCancelEdit={handleCancelEdit}
                                onSaveEdit={() => handleSaveEdit(keyword)}
                                onDelete={() => setDeleteConfirm(keyword)}
                                company={company}
                              />
                            ))
                          )}
                        </div>

                        {/* Keyword Count */}
                        <div style={{ 
                          marginTop: 16, 
                          paddingTop: 16, 
                          borderTop: '1px solid hsl(var(--border))',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: 'hsl(var(--muted-foreground))',
                          fontSize: 13
                        }}>
                          <span>
                            {searchQuery 
                              ? `Showing ${filteredKeywords.length} of ${keywords[company][lang].length} keywords`
                              : `${keywords[company][lang].length} ${lang === 'amharic' ? 'Amharic' : 'English'} keywords`
                            }
                          </span>
                          <span style={{ fontSize: 12 }}>
                            {lang === 'amharic' ? 'Tip: Amharic keywords help find Ethiopian government tenders' : 'Tip: Use specific phrases for better accuracy'}
                          </span>
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Keyword?</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "<strong>{deleteConfirm}</strong>"? 
                This keyword will no longer be used for searching or classification.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={() => handleDeleteKeyword(deleteConfirm)}
                data-testid="confirm-delete-btn"
              >
                <Trash2 size={14} />
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

export default Keywords;
